<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header pb-0 d-flex justify-content-between">
            <p class="mb-0 font-weight-bold">Editar Tarea #{{ taskId }}</p>
            <argon-button size="sm" color="secondary" @click="$router.go(-1)">Volver</argon-button>
          </div>
          <div class="card-body">
            <form @submit.prevent="updateTask">
              <div class="row">
                <div class="col-md-6">
                  <label class="form-label mb-1">Tipo de Tarea</label>
                  <select class="form-select" v-model="task.task_type">
                    <option v-for="type in taskTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
                  </select>
                </div>

                <div class="col-md-6">
                  <label class="form-label mb-1">Sucursal</label>
                  <select class="form-select" v-model="task.branch">
                    <option v-for="branch in branches" :key="branch.id" :value="branch.id">{{ branch.name }}</option>
                  </select>
                </div>

                <div class="col-md-12 mt-3">
                  <label class="form-label font-weight-bold">Ubicación (Arrastra el marcador)</label>
                  <div id="task-map" class="border-radius-lg shadow-sm mb-2" style="height: 300px; z-index: 1;"></div>
                  <div class="row">
                    <div class="col-6"><argon-input v-model="task.latitude" readonly size="sm" /></div>
                    <div class="col-6"><argon-input v-model="task.longitude" readonly size="sm" /></div>
                  </div>
                </div>

                <div class="col-md-12 mt-3">
                  <label class="form-control-label">Descripción</label>
                  <argon-textarea v-model="task.description" :rows="3" />
                </div>

                <div class="col-md-6 mt-3">
                  <label class="form-control-label">Fecha Límite</label>
                  <argon-input v-model="task.due_date" type="date" />
                </div>

                <div class="col-md-6 mt-3">
                  <label class="form-control-label">Prioridad</label>
                  <select class="form-select" v-model="task.priority">
                    <option value="Low">Baja</option>
                    <option value="Medium">Media</option>
                    <option value="High">Alta</option>
                  </select>
                </div>

                <hr class="horizontal dark my-4" />

                <div class="col-md-12">
                  <div class="form-check form-switch mb-3">
                    <input class="form-check-input" type="checkbox" id="assignTask" v-model="wantsToAssign" />
                    <label class="form-check-label font-weight-bold" for="assignTask">Asignación de Personal</label>
                  </div>
                </div>

                <div v-if="wantsToAssign" class="col-md-12 bg-light p-3 border-radius-lg">
                  <ArgonLookup :options="users" v-model="userSelected" track-by="id" display-key="full_name" />
                </div>
              </div>

              <argon-button type="submit" class="mt-4" color="info" fullWidth size="lg" :disabled="isSaving">
                {{ isSaving ? 'Guardando...' : 'Actualizar Tarea' }}
              </argon-button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonTextarea from "@/components/ArgonTextarea.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonLookup from "@/components/ArgonLookup.vue";
import axios from "axios";
import Swal from 'sweetalert2';
import L from 'leaflet';

export default {
  name: "EditTask",
  components: { ArgonInput, ArgonButton, ArgonTextarea, ArgonLookup },
  data() {
    return {
      taskId: this.$route.params.id,
      isSaving: false,
      wantsToAssign: false,
      map: null,
      marker: null,
      taskTypes: [],
      branches: [],
      users: [],
      userSelected: null,
      assignmentId: null, // Guardamos el ID de la asignación existente
      task: {
        id: null,
        task_type: null,
        branch: null,
        description: '',
        due_date: '',
        priority: '',
        latitude: null,
        longitude: null
      }
    };
  },
  watch: {
    userSelected(val) { this.assignmentUser = val ? val.id : null; }
  },
  methods: {
    async loadTaskData() {
      const token = localStorage.getItem("accessToken");
      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      const apiURL = this.$store.state.apiURL;

      try {
        // 1. Cargar Catálogos
        const [types, branches, users] = await Promise.all([
          axios.get(`${apiURL}task_type`, config),
          axios.get(`${apiURL}branches?company=${localStorage.getItem('companyId')}`, config),
          axios.get(`${apiURL}users?company=${localStorage.getItem('companyId')}`, config)
        ]);
        this.taskTypes = types.data;
        this.branches = branches.data;
        this.users = users.data;

        // 2. Cargar Datos de la Tarea (usando el reporte detallado filtrado por ID)
        const response = await axios.get(`${apiURL}reports/tasks`, {
          params: { type: 'user_detail', company_id: localStorage.getItem('companyId'), task_id: this.taskId },
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.data.length > 0) {
          const data = response.data[0];
          this.task = {
            id: data.id,
            task_type: data.task_type_id, // Asegúrate que el backend mande el ID
            branch: data.branch_id,
            description: data.task_description,
            due_date: data.due_date,
            priority: data.priority,
            latitude: data.latitude,
            longitude: data.longitude
          };

          // Si ya tiene un usuario asignado
          if (data.user_id) {
            this.wantsToAssign = true;
            this.assignmentId = data.assignment_id; // ID de la tabla task_assignment
            this.userSelected = this.users.find(u => u.id === data.user_id);
          }

          this.updateMapPosition();
        }
      } catch (e) { console.error(e); }
    },

    initializeMap() {
      this.map = L.map('task-map').setView([13.689, -89.187], 13);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(this.map);
      this.marker = L.marker([13.689, -89.187], { draggable: true }).addTo(this.map);

      this.marker.on('dragend', () => {
        const pos = this.marker.getLatLng();
        this.task.latitude = pos.lat.toFixed(8);
        this.task.longitude = pos.lng.toFixed(8);
      });
    },

    updateMapPosition() {
      if (this.task.latitude && this.task.longitude) {
        const pos = [this.task.latitude, this.task.longitude];
        this.map.setView(pos, 16);
        this.marker.setLatLng(pos);
      }
    },

    async updateTask() {
      this.isSaving = true;
      const token = localStorage.getItem("accessToken");
      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      const apiURL = this.$store.state.apiURL;

      try {
        // 1. PUT a la Tarea
        await axios.put(`${apiURL}task/${this.task.id}`, this.task, config);

        // 2. Manejar Asignación
        if (this.wantsToAssign && this.userSelected) {
          const assignPayload = {
            task: this.task.id,
            user: this.userSelected.id
          };

          if (this.assignmentId) {
            // Actualizar asignación existente
            assignPayload.id = this.assignmentId;
            await axios.put(`${apiURL}task_assignment`, assignPayload, config);
          } else {
            // Crear nueva asignación si no tenía
            await axios.post(`${apiURL}task_assignment`, assignPayload, config);
          }
        }

        Swal.fire('Actualizado', 'La tarea se guardó con éxito', 'success');
        this.$router.push('/tasks');
      } catch (err) {
        Swal.fire('Error', 'No se pudo actualizar', 'error');
      } finally {
        this.isSaving = false;
      }
    }
  },
  mounted() {
    this.initializeMap();
    this.loadTaskData();
  }
};
</script>